// src/components/multi-select.tsx

import { cva, type VariantProps } from 'class-variance-authority';
import { CheckIcon, ChevronDown, WandSparkles, XCircle, XIcon } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

/**
 * Variants for the multi-select component to handle different styles.
 * Uses class-variance-authority (cva) to define different styles based on "variant" prop.
 */
const multiSelectVariants = cva(
  'm-1 transition ease-in-out delay-150 hover:-translate-y-1 hover:scale-110 duration-300',
  {
    variants: {
      variant: {
        default: 'border-foreground/10 text-foreground bg-card hover:bg-card/80',
        secondary:
          'border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        inverted: 'inverted',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface IMultiSelectOption {
  /** The text to display for the option. */
  label: string;
  /** The unique value associated with the option. */
  value: string;
  /** Optional icon component to display alongside the option. */
  icon?: React.ComponentType<{ className?: string }>;
  /** Whether this option is disabled and cannot be selected. */
  disabled?: boolean;
  /**
   * Badge text shown on a disabled option explaining why it is disabled.
   * Falls back to "Inactive" — set this when the option is disabled for a
   * reason other than the item itself being inactive (e.g. "Not in campaign").
   */
  disabledBadge?: string;
}

/**
 * Props for MultiSelect component
 */
interface MultiSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof multiSelectVariants> {
  /**
   * An array of option objects to be displayed in the multi-select component.
   * Each option object has a label, value, and an optional icon.
   */
  options: IMultiSelectOption[];

  /**
   * Callback function triggered when the selected values change.
   * Receives an array of the new selected values.
   */
  onValueChange: (value: string[]) => void;

  /** The default selected values when the component mounts. */
  defaultValue?: string[];

  /**
   * Placeholder text to be displayed when no values are selected.
   * Optional, defaults to "Select options".
   */
  placeholder?: string;

  /**
   * Animation duration in seconds for the visual effects (e.g., bouncing badges).
   * Optional, defaults to 0 (no animation).
   */
  animation?: number;

  /**
   * Maximum number of items to display. Extra selected items will be summarized.
   * Optional, defaults to 3.
   */
  maxCount?: number;

  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean;

  /**
   * If true, renders the multi-select component as a child of another component.
   * Optional, defaults to false.
   */
  asChild?: boolean;

  /**
   * Additional class names to apply custom styles to the multi-select component.
   * Optional, can be used to add custom styles.
   */
  className?: string;
  contentClassName?: string;
  values?: string[];

  /**
   * When set, shows this label in the trigger instead of individual badges.
   * Useful when many items are selected by default and badges would clutter the UI.
   * The dropdown checkboxes still reflect the actual selection.
   */
  triggerLabel?: string;

  /**
   * If true, allows users to create custom options by typing and pressing Enter.
   * Optional, defaults to false.
   */
  creatable?: boolean;
}

export const MultipleSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      options,
      onValueChange,
      variant,
      placeholder = 'Select options',
      animation = 0,
      maxCount = 3,
      modalPopover = false,
      className,
      values,
      contentClassName,
      triggerLabel,
      creatable = false,
      ...props
    },
    ref,
  ) => {
    const [selectedValues, setSelectedValues] = React.useState<string[]>([]);
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [isAnimating, setIsAnimating] = React.useState(false);

    React.useEffect(() => {
      if (Array.isArray(values)) {
        setSelectedValues(values);
      }
    }, [values]);

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        if (creatable && event.currentTarget.value) {
          const newOptionValue = event.currentTarget.value.trim();
          if (newOptionValue && !options.some((opt) => opt.value === newOptionValue)) {
            const newSelectedValues = [...selectedValues, newOptionValue];
            setSelectedValues(newSelectedValues);
            onValueChange(newSelectedValues);
            event.currentTarget.value = '';
            event.preventDefault();
            return;
          }
        }
        setIsPopoverOpen(true);
      } else if (event.key === 'Backspace' && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues];
        newSelectedValues.pop();
        setSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      }
    };

    const toggleOption = (option: string) => {
      const optionData = options.find((opt) => opt.value === option);
      // Prevent selecting disabled options
      if (optionData?.disabled) {
        return;
      }
      const newSelectedValues = selectedValues.includes(option)
        ? selectedValues.filter((value) => value !== option)
        : [...selectedValues, option];
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const handleClear = () => {
      setSelectedValues([]);
      onValueChange([]);
    };

    const handleTogglePopover = () => {
      setIsPopoverOpen((prev) => !prev);
    };

    const clearExtraOptions = () => {
      const newSelectedValues = selectedValues.slice(0, maxCount);
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const toggleAll = () => {
      // Only consider non-disabled options for "Select All"
      const enabledOptions = options.filter((opt) => !opt.disabled);
      const enabledValues = enabledOptions.map((option) => option.value);
      const allEnabledSelected = enabledValues.every((value) => selectedValues.includes(value));
      
      if (allEnabledSelected) {
        // Deselect all enabled options
        const newSelectedValues = selectedValues.filter((value) => !enabledValues.includes(value));
        setSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      } else {
        // Select all enabled options (keep any disabled ones that were already selected)
        const newSelectedValues = [...new Set([...selectedValues, ...enabledValues])];
        setSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      }
    };

    return (
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal={modalPopover}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            {...props}
            onClick={handleTogglePopover}
            className={cn(
              'flex w-full p-1 rounded-md border min-h-9 h-auto items-center justify-between bg-inherit shadow-none hover:bg-inherit transition-all duration-200 [&_svg]:pointer-events-auto',
              className,
            )}
          >
            {triggerLabel ? (
              <div className='flex items-center justify-between w-full mx-auto'>
                <span className='text-[13px] text-muted-foreground mx-2 font-normal'>{triggerLabel}</span>
                <ChevronDown className='h-3.5 cursor-pointer text-muted-foreground mx-1.5 opacity-50' />
              </div>
            ) : selectedValues.length > 0 ? (
              <div className='flex justify-between items-center w-full'>
                <div className='flex flex-wrap items-center gap-1 overflow-hidden'>
                  {selectedValues.slice(0, maxCount).map((value) => {
                    const option = options.find((o) => o.value === value);
                    const IconComponent = option?.icon;
                    const isDisabled = option?.disabled ?? false;
                    return (
                      <Badge
                        className={cn(
                          'text-[11px] font-medium py-0 px-1.5 h-6 max-w-[140px]',
                          isDisabled && 'opacity-60 border-amber-300 bg-amber-50'
                        )}
                        key={value}
                        variant={isDisabled ? 'outline' : 'outline'}
                      >
                        {IconComponent && <IconComponent className='h-3 w-3 mr-1.5' />}
                        <span className={cn('truncate', isDisabled && 'text-amber-700')}>
                          {option?.label}
                        </span>
                        <XCircle
                          className={cn(
                            'ml-1.5 h-3 w-3',
                            isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                          )}
                          onClick={(event) => {
                            event.stopPropagation();
                            if (!isDisabled) {
                              toggleOption(value);
                            }
                          }}
                        />
                      </Badge>
                    );
                  })}
                  {selectedValues.length > maxCount && (
                    <Badge
                      className={cn(
                        'bg-slate-50 text-[11px] font-medium text-slate-600 border-slate-200 py-0 px-1.5 h-6 hover:bg-slate-100 transition-colors',
                        isAnimating ? 'animate-bounce' : '',
                      )}
                      style={{ animationDuration: `${animation}s` }}
                    >
                      {`+ ${selectedValues.length - maxCount} more`}
                      <XCircle
                        className='ml-1.5 h-3 w-3 cursor-pointer'
                        onClick={(event) => {
                          event.stopPropagation();
                          clearExtraOptions();
                        }}
                      />
                    </Badge>
                  )}
                </div>
                <div className='flex items-center flex-shrink-0'>
                  <XIcon
                    className='h-3.5 w-3.5 mx-1.5 cursor-pointer text-muted-foreground hover:text-slate-600 transition-colors'
                    onClick={(event) => {
                      event.stopPropagation();
                      handleClear();
                    }}
                  />
                  <Separator orientation='vertical' className='h-4' />
                  <ChevronDown className='h-3.5 w-3.5 mx-1.5 cursor-pointer text-muted-foreground opacity-50' />
                </div>
              </div>
            ) : (
              <div className='flex items-center justify-between w-full mx-auto'>
                <span className='text-[13px] text-muted-foreground mx-2 font-normal'>{placeholder}</span>
                <ChevronDown className='h-3.5 cursor-pointer text-muted-foreground mx-1.5 opacity-50' />
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn('w-80 p-0 !pointer-events-auto', contentClassName)}
          align='start'
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
        >
          <Command disablePointerSelection>
            <CommandInput placeholder='Search...' onKeyDown={handleInputKeyDown} />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                <CommandItem key='all' onSelect={toggleAll} className='cursor-pointer'>
                  <div
                    className={cn(
                      'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                      (() => {
                        const enabledOptions = options.filter((opt) => !opt.disabled);
                        const enabledValues = enabledOptions.map((opt) => opt.value);
                        return enabledValues.length > 0 && enabledValues.every((value) => selectedValues.includes(value));
                      })()
                        ? 'bg-primary text-primary-foreground'
                        : 'opacity-50 [&_svg]:invisible',
                    )}
                  >
                    <CheckIcon className='h-4 w-4' />
                  </div>
                  <span>(Select All)</span>
                </CommandItem>
                {options.map((option) => {
                  const isSelected = selectedValues.includes(option.value);
                  const isDisabled = option.disabled ?? false;
                  return (
                    <CommandItem
                      key={option.value}
                      onSelect={() => !isDisabled && toggleOption(option.value)}
                      className={cn(
                        'cursor-pointer',
                        isDisabled && 'opacity-50 cursor-not-allowed'
                      )}
                      disabled={isDisabled}
                    >
                      <div
                        className={cn(
                          'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                          isSelected
                            ? 'bg-primary text-primary-foreground'
                            : 'opacity-50 [&_svg]:invisible',
                        )}
                      >
                        <CheckIcon className='h-4 w-4' />
                      </div>
                      {option.icon && (
                        <option.icon className={cn(
                          'mr-2 h-4 w-4',
                          isDisabled ? 'text-slate-400' : 'text-muted-foreground'
                        )} />
                      )}
                      <span className={cn(isDisabled && 'text-slate-400')}>{option.label}</span>
                      {isDisabled && (
                        <span className='ml-auto text-xs text-amber-600'>{option.disabledBadge ?? 'Inactive'}</span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                <div className='flex items-center justify-between'>
                  {selectedValues.length > 0 && (
                    <>
                      <CommandItem
                        onSelect={handleClear}
                        className='flex-1 justify-center cursor-pointer'
                      >
                        Clear
                      </CommandItem>
                      <Separator orientation='vertical' className='flex min-h-6 h-full' />
                    </>
                  )}
                  <CommandItem
                    onSelect={() => setIsPopoverOpen(false)}
                    className='flex-1 justify-center cursor-pointer max-w-full'
                  >
                    Close
                  </CommandItem>
                </div>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
        {animation > 0 && selectedValues.length > 0 && (
          <WandSparkles
            className={cn(
              'cursor-pointer my-2 text-foreground bg-background w-3 h-3',
              isAnimating ? '' : 'text-muted-foreground',
            )}
            onClick={() => setIsAnimating(!isAnimating)}
          />
        )}
      </Popover>
    );
  },
);

MultipleSelect.displayName = 'MultipleSelect';
